import type { UseQueryResult } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { CleaningTaskDetailResponse } from '@/shared/api';
import { problemText } from '@/shared/api/problemText';
import { useApiProblem } from '@/shared/api/useApiProblem';
import {
  SurfaceLoadError,
  SurfaceLoading,
  SurfaceStaleHint,
} from '@/shared/components/SurfaceStates';

const CARD = 'border-hairline rounded-tile overflow-hidden border bg-white';

/**
 * Zustände der Aufgabe ohne geladenen Stand (lädt, keine Verbindung, fehlt, Fehler mit Grund) und —
 * `inline` — der Hinweis, wenn nur das Nachladen scheitert und der alte Stand stehen bleibt.
 */
export function WorkState({
  query,
  inline = false,
}: {
  query: UseQueryResult<CleaningTaskDetailResponse>;
  inline?: boolean;
}) {
  const problem = useApiProblem(query.error);
  const retry = () => void query.refetch();
  if (inline)
    return query.data && query.isError ? (
      <div className={CARD}>
        <SurfaceStaleHint onRetry={retry} retrying={query.isFetching} />
      </div>
    ) : null;
  if (query.isPending && query.fetchStatus === 'paused')
    return (
      <p role="status" className={`${CARD} text-ink-soft mx-auto max-w-xl p-5 text-sm`}>
        Keine Verbindung. Die Aufgabe lädt, sobald sie zurück ist.
      </p>
    );
  if (query.isPending)
    return (
      <div className={`${CARD} mx-auto w-full max-w-xl`}>
        <SurfaceLoading label="Aufgabe wird geladen" rows={4} />
      </div>
    );
  if (problem?.status === 404)
    return (
      <div role="alert" className={`${CARD} mx-auto flex max-w-xl flex-col gap-2 p-5`}>
        <b>Diese Aufgabe gibt es nicht mehr</b>
        <p className="text-ink-soft text-sm">
          Sie wurde entfernt oder liegt außerhalb deines Bereichs.
        </p>
        <Link
          to="/cleaning"
          search={{ view: undefined, date: undefined, status: undefined, assignee: undefined }}
          className="text-forest min-h-11 content-center text-sm font-bold underline-offset-4 hover:underline"
        >
          Zu den Aufgaben
        </Link>
      </div>
    );
  return (
    <div className={`${CARD} mx-auto max-w-xl`}>
      <SurfaceLoadError
        message={problemText(problem, 'Die Aufgabe konnte nicht geladen werden.')}
        onRetry={retry}
        retrying={query.isFetching}
      />
    </div>
  );
}
